import React, { useContext, Suspense, lazy } from 'react';
import styled, { keyframes } from 'styled-components';
import { Routes, Route, Navigate } from 'react-router-dom';
// Core first-load routes stay in the main bundle: Home + the full game flow.
import Home from './Home';
import StartScreen from './StartScreen';
import WinnerScreen from './WinnerScreen';
import GameActions from './GameActions';
import GameBoard from './GameBoard';
import MatchStats from './MatchStats';
import { AuthContext } from '../AuthContext';
import { Player, GameState, MatchStats as MatchStatsType, TournamentMatchContext } from '../types';

// Everything else is route-split so heavy libraries (chart.js, leaflet,
// react-quill, qrcode, broadcast package) only load when their page opens.
const AboutPage = lazy(() => import('./About'));
const WhereToPlay = lazy(() => import('./WhereToPlay'));
const Rules = lazy(() => import('./Rules'));
const BuyGame = lazy(() => import('./BuyGame'));
const Events = lazy(() => import('./Events'));
const Login = lazy(() => import('./Login'));
const Signup = lazy(() => import('./Signup'));
const Profile = lazy(() => import('./Profile'));
const Friends = lazy(() => import('./Friends'));
const FriendRequests = lazy(() => import('./FriendRequests'));
const AdminPage = lazy(() => import('./AdminPage'));
const TournamentList = lazy(() => import('./TournamentList'));
const TournamentDetail = lazy(() => import('./TournamentDetail'));
const TournamentManage = lazy(() => import('./TournamentManage'));
const TournamentScreen = lazy(() => import('./TournamentScreen'));
const TournamentMonitor = lazy(() => import('./TournamentMonitor'));
const LeagueList = lazy(() => import('./league/LeagueList'));
const LeagueDetail = lazy(() => import('./league/LeagueDetail'));
const SeasonPage = lazy(() => import('./league/SeasonPage'));
const Settings = lazy(() => import('./Settings'));
const TournamentSim = lazy(() => import('./TournamentSim'));

// ─── Branded route-loading fallback ───────────────────────────────────────────

const fallbackPulse = keyframes`
  0%, 100% { opacity: 0.35; transform: scale(1); }
  50%       { opacity: 1; transform: scale(1.12); }
`;

const FallbackWrap = styled.div`
  min-height: 60vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
`;

const FallbackDie = styled.div`
  font-size: 40px;
  animation: ${fallbackPulse} 1.1s ease-in-out infinite;
`;

const FallbackText = styled.div`
  font-family: 'Fjalla One', sans-serif;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.45);
  text-transform: uppercase;
  letter-spacing: 3px;
`;

const RouteFallback: React.FC = () => (
  <FallbackWrap role="status" aria-label="Loading page">
    <FallbackDie aria-hidden="true">🎲</FallbackDie>
    <FallbackText>Loading…</FallbackText>
  </FallbackWrap>
);

interface AppRoutesProps {
  players: Player[];
  setPlayers: React.Dispatch<React.SetStateAction<Player[]>>;
  gameWon: boolean;
  winnerName?: string;
  onStartGame: (
    playerList: { id: string; name: string }[],
    matchCount?: number,
    gameOptions?: { tournamentContext?: TournamentMatchContext }
  ) => void;
  clearGameState: () => void;
  onDiceValueChange: (diceIndex: number, value: number) => void;
  endTurn: () => void;
  toggleFreeze: (diceIndex: number) => void;
  soundEnabled: boolean;
  onUndo: () => void;
  onRestart: () => void;
  onQuit: () => void;
  canUndo: boolean;
  gameState: GameState;
  setGameState: React.Dispatch<React.SetStateAction<GameState>>;
  showMatchStats: boolean;
  setShowMatchStats: React.Dispatch<React.SetStateAction<boolean>>;
  startNewMatch: (newOrder?: { id: string; name: string }[]) => void;
  completeMatch: (matchStats: MatchStatsType) => void;
  isAllMatchesComplete: () => boolean;
  getFinalWinner: () => string | null;
  getTieInfo: () => { isTied: boolean; winners: any[]; maxWins: number } | null;
  botDifficulty?: 'easy' | 'medium' | 'hard';
}

const AppRoutes: React.FC<AppRoutesProps> = ({
  players,
  setPlayers,
  gameWon,
  winnerName,
  onStartGame,
  clearGameState,
  onDiceValueChange,
  endTurn,
  toggleFreeze,
  soundEnabled,
  onUndo,
  onRestart,
  onQuit,
  canUndo,
  gameState,
  setGameState,
  showMatchStats,
  setShowMatchStats,
  startNewMatch,
  completeMatch,
  isAllMatchesComplete,
  getFinalWinner,
  getTieInfo,
  botDifficulty,
}) => {
  const authContext = useContext(AuthContext);
  const { isAuthenticated, user } = authContext;

  const GameHeaderBar = styled.div`
    width: 100%;
    height: 40px;
    background: rgba(0, 13, 26, 1);
    display: flex;
    align-items: center;
    padding: 0 20px;
    color: #ffffff;
    font-family: 'Roboto Condensed', sans-serif;
    font-size: 16px;
    text-transform: uppercase;
  `;

  const MatchHeading = styled.div`
    width: 100%;
    height: 40px;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    color: rgba(255, 255, 255, 1);
    font-family: 'Fjalla One', sans-serif;
    font-size: 24px;
    font-weight: 400;
    line-height: 30px;
    text-align: center;
    text-transform: uppercase;
    text-shadow: 0 0 5px rgba(0, 0, 0, 1);
    background: radial-gradient(4050% 50% at 50% 50%, rgba(255, 255, 255, 0.08), rgba(0, 170, 255, 0) 100%);
    margin-bottom: 8px;
  `;

  // Player reordering is now handled by MatchStats → onStartNextMatch(newOrder)

  return (
    <Suspense fallback={<RouteFallback />}>
    <Routes>
      {/* Home Screen */}
      <Route path="/" element={<Home />} />

      {/* Start Screen */}
      <Route path="/lobby" element={<StartScreen onStartGame={onStartGame} />} />
      
      {/* Game Screen */}
      <Route
        path="/game"
        element={
          players.length > 0 ? (
            (gameState.isMatchBased && showMatchStats) ? (
              <MatchStats
                players={players}
                gameState={gameState}
                onStartNextMatch={(newOrder) => {
                  startNewMatch(newOrder);
                  setShowMatchStats(false);
                }}
                onQuit={onQuit}
              />
            ) : gameWon ? (
              <WinnerScreen
                winnerName={gameState.isMatchBased
                  ? (isAllMatchesComplete() ? getFinalWinner() || undefined : winnerName)
                  : winnerName}
                players={players}
                clearGameState={clearGameState}
                startGame={onStartGame}
                gameState={gameState}
                isMatchBased={gameState.isMatchBased}
                setPlayers={setPlayers}
              />
            ) : (
              <>
                <GameHeaderBar>TRIPLE FREEZER</GameHeaderBar>
                {gameState.isMatchBased && (
                  <MatchHeading>MATCH {gameState.currentMatch}</MatchHeading>
                )}
                <GameActions
                  onUndo={onUndo}
                  onRestart={onRestart}
                  onQuit={onQuit}
                  canUndo={canUndo}
                />
                <GameBoard
                  players={players}
                  onDiceValueChange={onDiceValueChange}
                  endTurn={endTurn}
                  toggleFreeze={toggleFreeze}
                  soundEnabled={soundEnabled}
                  botDifficulty={botDifficulty}
                />
              </>
            )
          ) : (
            <Navigate to="/" replace />
          )
        }
      />
      
      {/* Other Pages */}
      <Route path="/settings" element={<Settings />} />
      {/* Dev/QA: offline tournament simulator. Dev builds only — never mounted
          in a production build, so it is not reachable on the live site. */}
      {process.env.NODE_ENV !== 'production' && (
        <Route path="/sim" element={<TournamentSim />} />
      )}
      <Route path="/about" element={<AboutPage />} />
      <Route path="/rules" element={<Rules />} />
      <Route path="/buy" element={<BuyGame />} />
      <Route path="/events" element={<Events />} />
      <Route path="/where-to-play" element={<WhereToPlay />} />

      {/* Tournament Routes */}
      <Route path="/tournaments" element={<TournamentList />} />
      <Route
        path="/tournaments/:id"
        element={
          <TournamentDetail
            onStartGame={(playerList, matchCount = 1, opts) =>
              onStartGame(playerList, matchCount, opts)
            }
          />
        }
      />
      <Route
        path="/tournaments/:id/manage"
        element={
          isAuthenticated && user?.role === 'admin' ? (
            <TournamentManage />
          ) : (
            <Navigate to="/" replace />
          )
        }
      />
      <Route
        path="/tournaments/:id/monitor"
        element={
          isAuthenticated && user?.role === 'admin' ? (
            <TournamentMonitor />
          ) : (
            <Navigate to="/" replace />
          )
        }
      />
      <Route path="/tournaments/:id/screen" element={<TournamentScreen />} />

      {/* League Routes */}
      <Route path="/leagues" element={<LeagueList />} />
      <Route path="/leagues/:leagueId" element={<LeagueDetail />} />
      <Route path="/leagues/:leagueId/seasons/:seasonId" element={<SeasonPage />} />

      {/* Protected Admin Page */}
      <Route
        path="/admin"
        element={
          isAuthenticated && user?.role === 'admin' ? (
            <AdminPage />
          ) : (
            <Navigate to="/" replace />
          )
        }
      />

      {/* Auth Routes */}
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/profile" replace /> : <Login />}
      />
      <Route
        path="/profile/:uid"
        element={isAuthenticated ? <Profile /> : <Navigate to="/login" replace />}
      />
      <Route
        path="/profile"
        element={isAuthenticated ? <Profile /> : <Navigate to="/login" replace />}
      />
      <Route
        path="/friends"
        element={isAuthenticated ? <Friends /> : <Navigate to="/login" replace />}
      />
      <Route
        path="/friend-requests"
        element={isAuthenticated ? <FriendRequests /> : <Navigate to="/login" replace />}
      />
      <Route
        path="/signup"
        element={isAuthenticated ? <Navigate to="/profile" replace /> : <Signup />}
      />
    </Routes>
    </Suspense>
  );
};

export default AppRoutes;